import { NextPage, NextPageContext } from 'next';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import GalleryCarousel from '@/components/route/listing/Gallery/GalleryCarousel/GalleryCarousel';
import GalleryOverlay from '@/components/route/listing/Gallery/GalleryOverlay/GalleryOverlay';
import HeadMeta from '@/components/static-pages/HeadMeta';
import { PHOTOS } from '@/constants/urls';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { fetchOwner } from '@/redux/modules/owner/actions';
import {
  ownerRentalImagesSelector,
  ownerStayImagesSelector,
} from '@/redux/modules/owner/selectors';
import { IStore } from '@/utility/redux/store';
import { createOwnerUrlObj } from '@/utility/routes';

interface IError {
  statusCode: number;
  message?: string;
}

interface IProps {
  error?: IError | null;
}

const OwnerPhotos: NextPage<IProps> = () => {
  const router = useRouter();
  const { image } = useQueryParams(true);
  const rvImages = useSelector(ownerRentalImagesSelector);
  const stayImages = useSelector(ownerStayImagesSelector);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const images = useMemo(() => [...rvImages, ...stayImages], []);
  const [currentSlide, setCurrentSlide] = useState(() => {
    const imageIndex = images.findIndex(img => img.id === Number(image));
    return imageIndex || 0;
  });

  const intl = useIntl();

  const goToProfile = useCallback(() => {
    router.push(
      createOwnerUrlObj({ slug: router.query.slug }, slug => `/pro/${slug}`),
      undefined,
      { shallow: true },
    );
  }, [router]);

  const handleChange = (index: number) => {
    const imageId = images[index]?.id;
    if (imageId) {
      setCurrentSlide(index);
      router.replace(
        createOwnerUrlObj({ ...router.query, image: imageId }, slug => `/pro/${slug}${PHOTOS}`),
        undefined,
        { shallow: true },
      );
    }
  };

  return (
    <>
      <HeadMeta
        title={intl.formatMessage({
          defaultMessage: 'Owner Photos',
          id: 'ttv3wU',
        })}
        noindex
      />
      <main className="overflow-hidden">
        <GalleryOverlay goToListing={goToProfile}>
          <GalleryCarousel
            images={images}
            onChange={handleChange}
            activeSlide={currentSlide}
            ignoreImageChange
          />
        </GalleryOverlay>
      </main>
    </>
  );
};

OwnerPhotos.getInitialProps = async ({
  res,
  query,
  reduxStore,
}: NextPageContext & { reduxStore: IStore }) => {
  const { slug } = query;
  const state = reduxStore.getState();
  const owner = state.owner;

  if (typeof slug !== 'string' || !slug) {
    if (res) res.statusCode = 404;
    return { error: { statusCode: 404 } };
  }

  if (!owner.rentals.data.length) {
    await reduxStore.dispatch(fetchOwner(slug));
  }

  return {};
};

export default OwnerPhotos;
